import React from 'react';
import { Code2, Database, Cloud, Smartphone, Brain, Workflow, Link, Globe, Zap } from 'lucide-react';

const Technologies = () => {
  const techCategories = [
    {
      category: "Lenguajes de Programación",
      icon: Code2,
      color: "bg-blue-100 text-blue-600",
      technologies: ["PHP", "Python", "JavaScript", "C#", "Java", "TypeScript", "Go", "Rust"]
    },
    {
      category: "Frameworks & Libraries",
      icon: Cloud,
      color: "bg-teal-100 text-teal-600",
      technologies: ["Laravel", "React", "Next.js", "Node.js", "Django", "Vue.js", "Angular", ".NET Core"]
    },
    {
      category: "Bases de Datos",
      icon: Database,
      color: "bg-purple-100 text-purple-600",
      technologies: ["MySQL", "PostgreSQL", "MongoDB", "Redis", "SQLite", "Oracle", "SQL Server", "Supabase"]
    },
    {
      category: "Desarrollo Móvil",
      icon: Smartphone,
      color: "bg-orange-100 text-orange-600",
      technologies: ["React Native", "Flutter", "Ionic", "Android Studio", "Xcode", "Xamarin", "Kotlin", "Swift"]
    },
    {
      category: "Inteligencia Artificial",
      icon: Brain,
      color: "bg-green-100 text-green-600",
      technologies: ["OpenAI GPT", "TensorFlow", "PyTorch", "Scikit-learn", "Pandas", "NumPy", "Hugging Face", "LangChain"]
    },
    {
      category: "Automatización & APIs",
      icon: Workflow,
      color: "bg-red-100 text-red-600",
      technologies: ["N8N", "Zapier", "REST APIs", "GraphQL", "Webhooks", "Docker", "Kubernetes", "CI/CD"]
    }
  ];

  const certifications = [
    { name: "AWS Certified Solutions Architect", icon: "☁️" },
    { name: "Google Cloud Professional Developer", icon: "🔧" },
    { name: "Microsoft Azure Developer Associate", icon: "⚡" },
    { name: "Certified Kubernetes Administrator", icon: "🚀" }
  ];

  return (
    <section id="tecnologias" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Nuestras <span className="text-blue-600">Tecnologías</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Dominamos un amplio stack tecnológico que nos permite crear soluciones 
            robustas, escalables y adaptadas a las necesidades específicas de cada proyecto.
          </p>
        </div>

        {/* Technology Categories */}
        <div className="grid lg:grid-cols-2 gap-8 mb-20">
          {techCategories.map((category, index) => {
            const IconComponent = category.icon;
            return (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="flex items-center mb-6">
                  <div className={`w-14 h-14 ${category.color} rounded-xl flex items-center justify-center mr-4`}>
                    <IconComponent className="h-7 w-7" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{category.category}</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {category.technologies.map((tech, idx) => (
                    <div
                      key={idx}
                      className="bg-gray-50 px-3 py-2 rounded-lg text-sm text-gray-700 text-center hover:bg-gray-100 transition-colors duration-200 border border-gray-200 hover:border-gray-300"
                    >
                      {tech}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Key Technologies Highlight */}
        <div className="bg-gradient-to-r from-blue-600 to-teal-600 p-8 rounded-2xl text-white mb-20">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4">Especialización en Tecnologías Emergentes</h3>
            <p className="text-lg mb-8 opacity-90 max-w-3xl mx-auto">
              Nos mantenemos a la vanguardia tecnológica para ofrecer siempre las mejores soluciones a nuestros clientes.
            </p>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Brain className="h-8 w-8 text-white" />
                </div>
                <h4 className="font-semibold mb-1">IA Generativa</h4>
                <p className="text-sm opacity-80">OpenAI, GPT-4, Claude</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Link className="h-8 w-8 text-white" />
                </div>
                <h4 className="font-semibold mb-1">Blockchain</h4>
                <p className="text-sm opacity-80">Ethereum, Smart Contracts</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Globe className="h-8 w-8 text-white" />
                </div>
                <h4 className="font-semibold mb-1">Web3</h4>
                <p className="text-sm opacity-80">DApps, NFTs, DeFi</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Zap className="h-8 w-8 text-white" />
                </div>
                <h4 className="font-semibold mb-1">IoT & Edge</h4>
                <p className="text-sm opacity-80">Sensores, Edge Computing</p>
              </div>
            </div>
          </div>
        </div>

        {/* Certifications */}
        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">
            Certificaciones y Reconocimientos
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 text-center"
              >
                <div className="text-3xl mb-3">{cert.icon}</div>
                <h4 className="font-semibold text-gray-900 text-sm">{cert.name}</h4>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <p className="text-gray-600">
              Nuestro equipo se mantiene constantemente certificado en las últimas tecnologías 
              para garantizar la calidad y actualización de nuestras soluciones.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Technologies;